import * as z from 'zod/mini';
import {
  PATHWAY_CAPACITY_MAX,
  PATHWAY_NAME_MAX_LENGTH,
  PATHWAY_NOTES_MAX_LENGTH,
  PATHWAY_TRADE_SIZE_MAX_LENGTH,
} from '../constants.ts';
import { idSchema, sortOrderSchema, timestampSchema } from './common.ts';

// Pathways (ROADMAP I10): the conduits, trays, J-hooks, sleeves, and risers
// that cable runs travel through, each with a capacity — how many cables it
// should hold. A run may thread several pathways and a pathway carries many
// runs, so a pathway's fill is the count of its runs and one filled past its
// capacity raises an attention row.

export const PATHWAY_KINDS = [
  'conduit',
  'tray',
  'j_hook',
  'sleeve',
  'riser',
  'duct',
  'other',
] as const;
export type PathwayKind = (typeof PATHWAY_KINDS)[number];
export const pathwayKindSchema = z.enum(PATHWAY_KINDS);

export const PATHWAY_KIND_LABELS: Readonly<Record<PathwayKind, string>> = {
  conduit: 'Conduit',
  tray: 'Cable tray',
  j_hook: 'J-hook',
  sleeve: 'Sleeve',
  riser: 'Riser',
  duct: 'Duct',
  other: 'Other',
};

const pathwayFields = {
  name: z
    .string()
    .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(PATHWAY_NAME_MAX_LENGTH)),
  kind: pathwayKindSchema,
  /** How many cables it should hold; 0 when no ceiling is recorded. */
  capacity: z.int().check(z.gte(0), z.lte(PATHWAY_CAPACITY_MAX)),
  tradeSize: z.string().check(z.trim(), z.maxLength(PATHWAY_TRADE_SIZE_MAX_LENGTH)),
  notes: z.string().check(z.trim(), z.maxLength(PATHWAY_NOTES_MAX_LENGTH)),
};

export const pathwaySchema = z.object({
  id: idSchema,
  propertyId: idSchema,
  ...pathwayFields,
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Pathway = z.infer<typeof pathwaySchema>;

export const pathwayInputSchema = z.object({
  ...pathwayFields,
  capacity: z._default(pathwayFields.capacity, 0),
  tradeSize: z._default(pathwayFields.tradeSize, ''),
  notes: z._default(pathwayFields.notes, ''),
});
export type PathwayInput = z.infer<typeof pathwayInputSchema>;

export const pathwayPatchSchema = z.partial(z.object(pathwayFields));
export type PathwayPatch = z.infer<typeof pathwayPatchSchema>;

/** One run a pathway carries, named the way it appears everywhere else. */
export const pathwayMemberSchema = z.object({
  dropId: idSchema,
  cableId: z.string(),
  label: z.string(),
});
export type PathwayMember = z.infer<typeof pathwayMemberSchema>;

/** A pathway with the runs threaded through it: what the pathways page reads. */
export const pathwayViewSchema = z.object({
  ...pathwaySchema.shape,
  runs: z.array(pathwayMemberSchema),
});
export type PathwayView = z.infer<typeof pathwayViewSchema>;

export const pathwayListSchema = z.object({ pathways: z.array(pathwayViewSchema) });
export const pathwayEnvelopeSchema = z.object({ pathway: pathwayViewSchema });
